import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

import {
  fetchDocumentSettingById,
  addDocumentSetting,
  updateDocumentSetting,
} from "../../store/documentSettingStore/documentSettingThunk";
import { fetchDocumentTypes } from "../../store/documentTypeStore/documentTypeThunk";
import { fetchTemplates} from '../../store/invoiceStore/invoiceThunk';
import CustomTextField from "../FormFields/CustomTextField";
import CustomSelect, { IOptions } from "../FormFields/CustomSelect";
import { FormProvider, useForm } from "react-hook-form";
import { IDocumentSettingFormInputs } from "../../utils/documentSetting.types";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ApiStatus } from "../../utils/api.types";
import LoadingButton from "@mui/lab/LoadingButton";

interface DialogDocumentSettingFormProps {
  title?: string;
  documentSettingId?: string;
  accountId?: string;
  open: boolean;
  onClose: (k: any) => void;
  setDocumentType?: string;
}

const DialogDocumentSettingForm: FC<DialogDocumentSettingFormProps> = ({
  title,
  onClose,
  documentSettingId,
  accountId,
  open,
  setDocumentType
}) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [documentTypeOptions, setDocumentTypeOptions] = useState<IOptions[]>([]);
  const [documentTypeValue, setDocumentTypeValue] = useState("");

  const [templateOptions, setTemplateOptions] = useState<IOptions[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const [documentSettingData, setDocumentSettingData] = useState<any>();
  // const [legalName, setLegalName] = useState('');
  const [numberLengthValue, setNumberLengthValue] = useState<any>();
  const [sequenceValue, setSequenceValue] = useState<any>();

  // Schema to validate account form.
  const schema = yup.object().shape({
    documentType: yup.string().required("Document type is required."),
    // docNumberPrefix: yup.string().required("Document number prefix is required."),
    docNumberPrefix: yup.string(),
    footer1: yup.string().required("Footer 1 length is required."),
    footer2: yup.string().required("Footer 2 length is required."),
    docNumberLength: yup
      .number()
      .typeError('Document length is required and should be a number.')
      // .min(5, "Enter 5 or above")
      // .max(10, "Cannot exceed")
      .required("Document length is required."),

    docSequenceNumber: yup.number()
      .typeError('Sequence number is required.')
      .required("Sequence number is required."),
    // .min(5, "Enter 5 or above")
    // .max(10, "Cannot exceed")
  });

  //form
  const methods = useForm<IDocumentSettingFormInputs>({
    mode: "onTouched",
    resolver: yupResolver(schema),
  });

  const { status } = useAppSelector((state) => state.documentSettings);
  // const { documentTypes, status: documentTypeStatus, errorMessage: documentTypeErrorMessage } = useAppSelector(state => state.documentType);

  useEffect(() => {
    const getDocumentSettingDetails = async () => {
      if (documentSettingId) {
        const bd: any = await dispatch(
          fetchDocumentSettingById(documentSettingId)
        );

        setDocumentSettingData(bd.payload);
        setNumberLengthValue(bd.payload.docNumberLength);
        setSequenceValue(bd.payload.docSequenceNumber);

        methods.setValue("docNumberPrefix", bd.payload.docNumberPrefix);
        methods.setValue("docSequenceNumber", bd.payload.docSequenceNumber);
        methods.setValue("docNumberLength", bd.payload.docNumberLength);
        methods.setValue("termsAndConditions", bd.payload.termsAndConditions);
        methods.setValue("paymentDetails", bd.payload.paymentDetails);
        methods.setValue("paymentTermDays", bd.payload.paymentTermDays);
        methods.setValue("notes", bd.payload.notes);
        methods.setValue("footer1", bd.payload.footer1);
        methods.setValue("footer2", bd.payload.footer2);
        methods.setValue("smsAndWhatsAppText", bd.payload.smsAndWhatsAppText);
        methods.setValue("emailSubject", bd.payload.emailSubject);
        methods.setValue("emailText", bd.payload.emailText);
        methods.setValue("documentType", bd.payload.documentType?._id || "");
        setDocumentTypeValue(bd.payload.documentType?._id || "");
        methods.setValue("defaultPdfTemplate", bd.payload.defaultPdfTemplate);
        setSelectedTemplate(bd.payload.defaultPdfTemplate?._id || "");
      } else {
        methods.setValue("footer1", "Powered by wareozo");
        methods.setValue("footer2", "Powered by wareozo");
        methods.setValue("paymentDetails", "Net-30");
      }
    };

    getDocumentSettingDetails();
  }, [documentSettingId, dispatch, methods]);

  useEffect(() => {
    const getDocumentTypes = async () => {
      if (!documentTypeOptions.length) {
        const data: any = await dispatch(fetchDocumentTypes());

        if (setDocumentType && !documentSettingId) {
          let obj = data.payload.find((o: { name: string; }) => o.name.toLowerCase() === setDocumentType);
          console.log(obj._id);
          setDocumentTypeValue(obj._id);
        }

        setDocumentTypeOptions(
          data.payload.map((c: any) => {
            return { label: c.name, value: c._id };
          })
        );
      }
    };
    const getTemplates = async () => {
      if (!templateOptions.length) {
        const data: any = await dispatch(fetchTemplates());

        if(data.payload.length > 0) {
          setTemplateOptions(
            data.payload.map((c: any) => {
              return { label: c.template, value: c._id };
            })
          );
        }
      }
    };

    if (open) {
      getDocumentTypes();
      getTemplates()
    }
  }, [open, dispatch, documentTypeOptions, templateOptions, documentSettingId, setDocumentType]);

  const updateDocumentSettingDetails = async (e: any) => {
    if (documentSettingId) {
      e.account = documentSettingData?.account || '';

      const resData = await dispatch(
        updateDocumentSetting({
          documentSettingPayload: e,
          id: documentSettingId,
        })
      );

      //console.log(resData)
      const resMsg = resData.payload?.message?.toUpperCase();
      if (resMsg && resMsg.includes('SUCCESS')) {
        onClose(true);
      }
    }
  };
  const createDocumentSettingDetails = async (e: any) => {
    let _accountId = params.accountId || accountId;
    const resData = await dispatch(
      addDocumentSetting({ ...e, ...{ account: _accountId } })
    );

    //console.log(resData)
    if (status === ApiStatus.SUCCESS) {
      onClose(true);
    }
  };

  const onSubmit = (e: any) => {
    e.docSequenceNumber = sequenceValue;

    if (documentSettingId) {
      updateDocumentSettingDetails(e);
      return;
    }

    createDocumentSettingDetails(e);
  };

  const handleSequenceOnChange = (ev: any) => {
    let dVal = '';
    if (numberLengthValue < ev.target.value.length) {
      let temp = ev.target.value.length - numberLengthValue;
      let temp2 = ev.target.value.substring(temp);
      setSequenceValue(temp2);
      dVal = temp2;
    } else {
      setSequenceValue(ev.target.value);
      dVal = ev.target.value;
    }

    methods.setValue("docSequenceNumber", dVal, { shouldValidate: true });
  };

  const handleSequenceOnBlur = (ev: any) => {
    if (numberLengthValue >= ev.target.value.length) {
      let newSequenceValue = String(1).padStart(
        numberLengthValue,
        "0"
      );
      setSequenceValue(newSequenceValue);

      methods.setValue("docSequenceNumber", newSequenceValue, { shouldValidate: true });
    }
  };

  const handleNumberLengthChange = (e: any) => {

    setNumberLengthValue(e.target.value);
    setSequenceValue("");

    methods.setValue("docNumberLength", e.target.value, { shouldValidate: true });

  };
  return (
    <div className="drawer-form-container">
      <FormProvider {...methods}>
        {(!documentSettingId || (documentSettingId && documentSettingData)) && (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100vh", padding: "16px 0 16px 16px" }}
            component="form"
            onSubmit={methods.handleSubmit(onSubmit)}
            noValidate
          >
            <DialogTitle
              sx={{
                height: "0px",
                mt: 1,
                mb: 1,
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography variant="h5">
                  {documentSettingId
                    ? "Update Document Settings"
                    : "Add Document Settings"}
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent>
              <Grid
                container
                justifyContent="center"
                spacing={2}
                rowSpacing={-2}
              >
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  {documentTypeOptions?.length && (
                    <CustomSelect
                      name="documentType"
                      label="Document Type*"
                      id="documentType"
                      variant="standard"
                      required
                      options={documentTypeOptions}
                      value={documentTypeValue ?? ""}
                      onChange={(event: SelectChangeEvent<any>) => {
                        methods.setValue("documentType", event.target.value, { shouldValidate: true });
                        setDocumentTypeValue(event.target.value);
                      }}
                      disabled={documentSettingId ? true : false}
                    />
                  )}
                </Grid>

                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Number Prefix"
                    name="docNumberPrefix"
                    required
                    variant="standard"
                  />
                </Grid>

                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    type="number"
                    label="Number Length"
                    name="docNumberLength"
                    required
                    variant="standard"
                    value={numberLengthValue}
                    onChange={handleNumberLengthChange}
                    onBlur={handleSequenceOnBlur}
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    type="number"
                    label="Sequence Number"
                    name="docSequenceNumber"
                    variant="standard"
                    value={sequenceValue}
                    inputProps={{ maxLength: Number(numberLengthValue || '10') }}
                    onChange={handleSequenceOnChange}
                    // onBlur={handleSequenceOnBlur}
                  />
                </Grid>
                
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  {documentTypeOptions?.length && (
                    <CustomSelect
                      name="defaultPdfTemplate"
                      label="Default PDF Template*"
                      id="defaultPdfTemplate"
                      variant="standard"
                      required
                      options={templateOptions}
                      value={selectedTemplate ?? ""}
                      onChange={(event: SelectChangeEvent<any>) => {
                        methods.setValue("defaultPdfTemplate", event.target.value, { shouldValidate: true });
                        setSelectedTemplate(event.target.value);
                      }}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Footer 1"
                    multiline
                    name="footer1"
                    required
                    variant="standard"
                  />
                </Grid>

                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Footer 2"
                    multiline
                    name="footer2"
                    required
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="SMS & WhatsApp Text"
                    multiline
                    name="smsAndWhatsAppText"
                    variant="standard"
                  />
                </Grid>

                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Email Subject"
                    multiline
                    name="emailSubject"
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Email Text"
                    multiline
                    name="emailText"
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Payment Term"
                    name="paymentDetails"
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    type="number"
                    label="Payment Term/Expiry Days"
                    name="paymentTermDays"
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Terms & Conditions"
                    multiline
                    name="termsAndConditions"
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} md={12} sm={12} lg={12}>
                  <CustomTextField
                    label="Notes/Disclaimer"
                    multiline
                    name="notes"
                    variant="standard"
                  />
                </Grid>
              </Grid>
            </DialogContent>

            <DialogActions sx={{ height: "40px", mb: 2 }}>
              <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
                <Grid item xs={12} md={12} sm={12} lg={12} display="flex" justifyContent="flex-end" >
                  <Button
                    style={{ marginRight: '10px' }}
                    variant="outlined"
                    onClick={() => {
                      methods.reset();
                      onClose(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <LoadingButton
                    type="submit"
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    variant="contained"
                  >
                    {documentSettingId ? "Update" : "Create"}
                  </LoadingButton>
                </Grid>
              </Grid>
            </DialogActions>
          </Box>
        )}
      </FormProvider>
    </div>
  );
};

export default DialogDocumentSettingForm;
